import { useTranslations } from "next-intl";

import { RichContent } from "@/components/rich-content";
import { hasHtmlContent } from "@/lib/html-content";
import { cn } from "@/lib/utils";

/** Any element at all means the explanation was written in the editor. */
const HAS_ELEMENT = /<\/?[a-z][^>]*>/i;

/**
 * The prose under one sample. Written in `ProgsityEditor` since 2026-11-05, so
 * it can hold a picture, a table, a formula or a diagram, and it renders the
 * way the statement does. An explanation saved before then is plain text and
 * keeps its line breaks.
 */
export function SampleExplanation({
  html,
  className,
  tone = "light",
}: {
  html: string | null | undefined;
  className?: string;
  /** `dark` for the quiz coding task, whose statement sits on a dark panel. */
  tone?: "light" | "dark";
}) {
  const t = useTranslations("judge.problem");
  if (!hasHtmlContent(html)) return null;
  const dark = tone === "dark";
  return (
    <div
      className={cn(
        "rounded-lg border px-4 py-3",
        dark ? "border-white/10 bg-white/[0.03]" : "border-[rgb(var(--border))] bg-neutral-50/70",
        className,
      )}
    >
      <p
        className={cn(
          "text-xs font-semibold uppercase tracking-[0.06em]",
          dark ? "text-white/45" : "text-[rgb(var(--muted-foreground))]",
        )}
      >
        {t("sampleExplanation")}
      </p>
      {HAS_ELEMENT.test(html) ? (
        <RichContent
          html={html}
          className={cn("mt-1.5 text-sm leading-7", dark ? "text-white/75" : "text-neutral-700")}
        />
      ) : (
        <p
          className={cn(
            "mt-1.5 whitespace-pre-line text-sm leading-7",
            dark ? "text-white/75" : "text-neutral-700",
          )}
        >
          {html}
        </p>
      )}
    </div>
  );
}
